import React, { FC, useState } from 'react'
import { RouteComponentProps, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'

import { sendEmailVerificationAPI } from '@apiRoutes'
import { LinkButton, Footer, Header, Layout, LoginButton } from '@components'
import { navigateToUrl, routes, useAuthToken } from '@utils'
import airplaneImage from './assets/airplane.png'
import backgroundAirplaneImage from './assets/background_airplane.png'
import model from './model.en.json'
import styles from './styles.module.scss'

const EmailVerification: FC<RouteComponentProps> = ({ history }) => {
  // Get current user info
  // Get all values from model json
  const {
    backgroundAirplaneAlternative,
    airplaneAlternative,
    title,
    titleDescription,
    resendEmailText,
    resendEmailTextLink,
    resendEmailTextDot,
    resendEmailTextSuccess,
    titleEmailVerificationLinkExpired,
    descriptionEmailVerificationLinkExpired,
    captionLoginButton
  } = model

  // State hook to show success message
  const [isLoading, setIsLoading] = useState(false)

  // State hook to show success message
  const [showMessage, setShowMessage] = useState(false)

  // Get current token
  const { tokenPending, authToken } = useAuthToken()

  // Get current user
  const { user } = useAuth0()

  // Handle resend email event the resend email event
  const handleResendEmailClick = async () => {
    try {
      // Verify the token and user are availables
      if (tokenPending || !authToken || !user) return
      // Invoke send email verification API
      setIsLoading(true)
      const result = await sendEmailVerificationAPI(authToken)
      setIsLoading(false)
      // Show success message
      const success = result.ok || result.success
      setShowMessage(success)
      setTimeout(() => {
        setShowMessage(false)
      }, 4000)
    } catch (error) {
      // ToDo - Show and log the error if exists
    }
  }

  // Success message
  const Message: FC<{ show: boolean; message: string }> = ({
    show,
    message
  }) => {
    if (!show) {
      return null
    }
    return (
      <p>
        <span className={styles.success}>{message}</span>
      </p>
    )
  }

  // Resend email verification
  const ResendEmailVerification: FC = () => {
    return (
      <div className={styles.container}>
        <Header title={title} description={titleDescription} />
        <div className={styles.body}>
          <div className={styles.airplaneImageContainer}>
            <img
              className={styles.backgroundAirplane}
              src={backgroundAirplaneImage}
              alt={backgroundAirplaneAlternative}
              srcSet={backgroundAirplaneImage}
            />
            <img
              className={styles.airplane}
              src={airplaneImage}
              alt={airplaneAlternative}
              srcSet={airplaneImage}
            />
          </div>
          <p>
            <span>{resendEmailText}</span>
            <LinkButton
              className={styles.linkButton}
              caption={resendEmailTextLink}
              disabled={showMessage}
              onClick={handleResendEmailClick}
              isLoading={isLoading}
            />
            {resendEmailTextDot}
          </p>
          <Message message={resendEmailTextSuccess} show={showMessage} />
        </div>
        <Footer />
      </div>
    )
  }

  // Email verification link expired
  const EmailVerificationLinkExpired: FC = () => {
    return (
      <div className={styles.container}>
        <Header
          title={titleEmailVerificationLinkExpired}
          description={descriptionEmailVerificationLinkExpired}
        />
        <LoginButton
          className={styles.logInButton}
          caption={captionLoginButton}
        />
        <Footer />
      </div>
    )
  }

  // Main content
  const MainContent = () => {
    let render = <ResendEmailVerification />
    // Get parameter
    const query = new URLSearchParams(useLocation().search)
    const success = query.get('success')?.toLocaleLowerCase() === 'true'
    const message = query.get('message')

    const URLQueryStringsMessage = {
      verification_success:
        'Your email was verified. You can continue using the application.',
      verification_expired: 'Access expired.',
      verification_already: 'This account is already verified.',
      verification_used_once: 'This URL can be used only once',
      token_expired: 'token_expired'
    }
    if (!success) {
      switch (message) {
        case URLQueryStringsMessage.verification_expired:
          render = <EmailVerificationLinkExpired />
          break
        case URLQueryStringsMessage.verification_already:
          break
        case URLQueryStringsMessage.verification_used_once:
          break
        case URLQueryStringsMessage.token_expired:
          break
        default:
          break
      }
    }
    // Redirect To Setup Caregiver Account if user has email verified
    if (user?.email_verified) {
      navigateToUrl(history, routes.setupCaregiverProfile)
    }
    return render
  }

  return (
    <Layout>
      <MainContent />
    </Layout>
  )
}

export default EmailVerification
